import React, { useCallback, useMemo } from 'react';
import { Linking, View, useWindowDimensions } from 'react-native';
import HtmlRenderer from 'react-native-render-html';

import Text from '../../../components/text/Text';
import { useTheme } from '../../../theme';

const HEADING_TAGS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];

/**
 * A run brief's HTML in the redesign's text styles, for a card padded with
 * space.lg inside the screen or sheet gutters (BriefSheet, run overview).
 * Props: html
 */
const BriefHtml = ({ html }) => {
	const { theme: { ui } } = useTheme();
	const { width } = useWindowDimensions();

	const handleLinkPress = useCallback((_, href) => Linking.openURL(href), []);
	const htmlStyles = useMemo(() => {
		const textStyle = { color: ui.colors.text };
		const tagsStyles = {
			p: { ...textStyle, marginTop: 0, marginBottom: ui.space.sm },
			span: textStyle,
			div: textStyle,
			li: textStyle,
			a: { color: ui.colors.accent },
			b: { fontFamily: ui.fontFamily('700') },
			strong: { fontFamily: ui.fontFamily('700') },
		};
		HEADING_TAGS.forEach((tag) => {
			tagsStyles[tag] = { ...textStyle, fontFamily: ui.fontFamily('600') };
		});

		const bulletSize = ui.type.body.fontSize / 2.8;

		return {
			baseFontStyle: { ...ui.type.body, color: ui.colors.text },
			tagsStyles,
			// The library draws list markers in black, which disappears on the dark themes.
			listsPrefixesRenderers: {
				ul: () => (
					<View
						style={{
							width: bulletSize,
							height: bulletSize,
							marginTop: ui.type.body.fontSize / 2,
							marginRight: 10,
							borderRadius: bulletSize,
							backgroundColor: ui.colors.text,
						}}
					/>
				),
				ol: (htmlAttribs, children, convertedCSSStyles, { index }) => (
					<Text style={{ ...ui.type.body, color: ui.colors.text, marginRight: 5 }}>{`${index + 1})`}</Text>
				),
			},
		};
	}, [ui]);

	return (
		<HtmlRenderer
			html={html}
			// Gutters plus the card padding on both sides.
			imagesMaxWidth={Math.min(width, ui.layout.maxContentWidth) - 2 * (ui.space.gutter + ui.space.lg)}
			onLinkPress={handleLinkPress}
			baseFontStyle={htmlStyles.baseFontStyle}
			tagsStyles={htmlStyles.tagsStyles}
			listsPrefixesRenderers={htmlStyles.listsPrefixesRenderers}
		/>
	);
};

export default BriefHtml;
